import {
  IonButton,
  IonInput,
  IonItem,
  IonSelect,
  IonSelectOption,
  IonTextarea,
  IonToggle,
} from '@ionic/react';
import React from 'react';
import {
  AUTOMATION_BLOCK_CATALOG,
  getBlockDefinition,
  type AutomationBlockParamDef,
} from '../../automation/blockCatalog';
import type { AutomationGraphNode } from '../../types/AutomationWorkflow.types';
import { pickDirectory } from '../../helper/pickDirectory';

const renderParamField = (
  key: string,
  param: AutomationBlockParamDef,
  value: unknown,
  onChange: (next: Record<string, unknown>) => void,
): React.ReactNode => {
  const currentValue = value ?? param.default ?? '';

  switch (param.type) {
    case 'boolean':
      return (
        <IonItem key={key} lines="none">
          <IonToggle
            checked={Boolean(currentValue)}
            onIonChange={(event) => onChange({ [key]: event.detail.checked })}
          >
            {param.label}
          </IonToggle>
        </IonItem>
      );
    case 'select':
      return (
        <IonItem key={key} lines="none">
          <IonSelect
            label={param.label}
            labelPlacement="stacked"
            value={String(currentValue)}
            onIonChange={(event) => onChange({ [key]: event.detail.value })}
          >
            {(param.options ?? []).map((option) => (
              <IonSelectOption key={option.value} value={option.value}>
                {option.label}
              </IonSelectOption>
            ))}
          </IonSelect>
        </IonItem>
      );
    case 'textarea':
      return (
        <IonItem key={key} lines="none">
          <IonTextarea
            label={param.label}
            labelPlacement="stacked"
            value={String(currentValue)}
            rows={8}
            onIonInput={(event) =>
              onChange({ [key]: event.detail.value ?? '' })
            }
          />
        </IonItem>
      );
    case 'folder':
      return (
        <IonItem key={key} lines="none">
          <IonInput
            label={param.label}
            labelPlacement="stacked"
            value={String(currentValue)}
            placeholder={param.placeholder}
            onIonInput={(event) =>
              onChange({ [key]: event.detail.value ?? '' })
            }
          />
          <IonButton
            slot="end"
            fill="outline"
            onClick={() =>
              void pickDirectory().then((folderPath) => {
                if (folderPath) onChange({ [key]: folderPath });
              })
            }
          >
            Choose
          </IonButton>
        </IonItem>
      );
    default:
      return (
        <IonItem key={key} lines="none">
          <IonInput
            label={param.label}
            labelPlacement="stacked"
            value={String(currentValue)}
            placeholder={
              param.allowVariableRef
                ? (param.placeholder ?? 'Or use $start.filePath')
                : param.placeholder
            }
            onIonInput={(event) =>
              onChange({ [key]: event.detail.value ?? '' })
            }
          />
        </IonItem>
      );
  }
};

export const BlockConfigForm: React.FC<{
  node: AutomationGraphNode;
  onChange: (config: Record<string, unknown>) => void;
  readOnlyMessage?: string;
  variant?: 'panel' | 'inline';
}> = ({ node, onChange, readOnlyMessage, variant = 'panel' }) => {
  if (readOnlyMessage) {
    return <p>{readOnlyMessage}</p>;
  }

  if (node.type === 'start' || node.type === 'trigger' || node.type === 'end') {
    return (
      <p>
        {node.type === 'end'
          ? 'End marks where the workflow finishes.'
          : 'Configure the trigger in the builder.'}
      </p>
    );
  }

  const definition = getBlockDefinition(node.type);
  if (!definition) return null;

  const visibleParams = Object.entries(definition.inputParams).filter(
    ([key, param]) => {
      if (node.type !== 'read_write_tags') return true;
      const mode = String(node.config.mode ?? 'read');
      if (mode === 'read') {
        return key === 'mode';
      }
      return [
        'mode',
        'title',
        'artist',
        'album',
        'genre',
        'grouping',
        'comment',
      ].includes(key);
    },
  );

  const content = (
    <>
      {variant === 'panel' && (
        <p className="automation-block-description">{definition.description}</p>
      )}
      {visibleParams.map(([key, param]) =>
        renderParamField(key, param, node.config[key], onChange),
      )}
      {node.type === 'import_to_library' && variant === 'panel' && (
        <p className="automation-block-hint">
          Uses file paths passed from the previous step.
        </p>
      )}
    </>
  );

  if (variant === 'inline') {
    return <div className="automation-block-config-inline">{content}</div>;
  }

  return content;
};

export const getBlockPalette = () => AUTOMATION_BLOCK_CATALOG;
